// components/data-quality/CustomerQualityComparison.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CustomerQualityComparison = ({ customerStats }) => {
  // Limit to top 10 customers by booking count
  const topCustomers = [...customerStats]
    .sort((a, b) => b.totalBookings - a.totalBookings)
    .slice(0, 10);

  // Data for horizontal bar chart
  const chartData = {
    labels: topCustomers.map((item) => {
      // Display customer ID and short name
      const shortName =
        item.customerName.length > 25
          ? `${item.customerId} - ${item.customerName.substring(0, 22)}...`
          : `${item.customerId} - ${item.customerName}`;
      return shortName;
    }),
    datasets: [
      {
        label: "Quality Score",
        data: topCustomers.map((item) => item.averageQualityScore),
        backgroundColor: topCustomers.map((item) => {
          const score = item.averageQualityScore;
          if (score >= 90) return "rgba(34, 197, 94, 0.8)"; // Green
          if (score >= 70) return "rgba(59, 130, 246, 0.8)"; // Blue
          if (score >= 50) return "rgba(234, 179, 8, 0.8)"; // Yellow
          if (score >= 30) return "rgba(249, 115, 22, 0.8)"; // Orange
          return "rgba(239, 68, 68, 0.8)"; // Red
        }),
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: "Abandoned %",
        data: topCustomers.map((item) => item.abandonedPercentage),
        backgroundColor: "rgba(239, 68, 68, 0.6)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  // Options for horizontal bar chart
  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.parsed.x.toFixed(1)}%`;
          },
          afterLabel: (context) => {
            const customer = topCustomers[context.dataIndex];
            return [
              `Bookings: ${customer.totalBookings}`,
              `Valid Status: ${customer.validStatusPercentage.toFixed(1)}%`,
              `Valid Dates: ${customer.validDatePercentage.toFixed(1)}%`,
            ];
          },
          title: (tooltipItems) => {
            const index = tooltipItems[0].dataIndex;
            const customer = topCustomers[index];
            return customer.customerName;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: "Percentage (%)",
          font: {
            size: 11,
          },
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
      y: {
        ticks: {
          font: {
            size: 10,
          },
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faBuilding} className="text-blue-600 mr-2" />
          Customer Quality Comparison
        </h3>
        <div className="text-sm text-gray-500">
          Top {topCustomers.length} customers
        </div>
      </div>

      <div className="flex-grow">
        <Bar data={chartData} options={chartOptions} />
      </div>

      <div className="mt-2 text-xs text-gray-500">
        <p>
          Customer comparison allows identification of clients with data quality
          issues.
        </p>
      </div>
    </div>
  );
};

export default CustomerQualityComparison;
